/**
 * assets/custom-hero-grid.js
 * ==========================
 * Mobiel-carousel gedrag voor de "Custom: Hero Grid" sectie.
 *
 * Op schermen <= 749px worden de grid-cellen een full-width scroll-snap
 * carousel. Staat autoplay aan (data-autoplay in seconden), dan schuift de
 * slider automatisch door naar het volgende kaartje en loopt aan het eind
 * terug naar het eerste. Op desktop is het element inert en blijft de
 * CSS-grid layout (en bijbehorende semantiek) ongewijzigd.
 *
 * Toegankelijkheid (alleen actief binnen de mobiele breakpoint):
 * - host krijgt role="group" + aria-roledescription="carousel" + naam;
 * - elke slide krijgt role="group" + aria-roledescription="slide" + label;
 * - aria-live schakelt tussen "off" (auto-rotatie) en "polite" (gepauzeerd);
 * - autoplay stopt definitief zodra de gebruiker de slider bedient
 *   (swipe of stip) — er volgt geen automatische herstart;
 * - autoplay is uit bij prefers-reduced-motion, verborgen tab en in de editor.
 *
 * Progressive enhancement: zonder JS blijft de carousel handmatig
 * swipebaar (scroll-snap), enkel zonder autoplay of stip-navigatie.
 *
 * Gebruikergerichte teksten komen via data-attributen uit Liquid, zodat ze
 * gelokaliseerd zijn. In data-slide-label worden de tokens __N__ (slide-index)
 * en __T__ (totaal aantal slides) vervangen.
 */

const MOBILE_QUERY = '(max-width: 749px)';
const SCROLL_SETTLE_MS = 400;

class CustomHeroGridSlider extends HTMLElement {
  #timer = null;
  #scrollSettle = null;
  #index = 0;
  #suppressScrollSync = false;
  #userPaused = false;

  connectedCallback() {
    this.slides = Array.from(this.children);
    if (this.slides.length <= 1) return;

    this.dots = this.parentElement?.querySelector('[data-hero-grid-dots]') ?? null;
    this.interval = (parseFloat(this.dataset.autoplay) || 0) * 1000;
    this.mql = window.matchMedia(MOBILE_QUERY);
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.carouselRoleDescription = this.dataset.carouselRoledescription || 'carousel';
    this.slideRoleDescription = this.dataset.slideRoledescription || 'slide';
    this.carouselLabel = this.dataset.carouselLabel || '';
    this.slideLabelTemplate = this.dataset.slideLabel || 'Slide __N__ of __T__';

    this.#buildDots();

    this.onScroll = this.#onScroll.bind(this);
    this.onResize = this.#sync.bind(this);
    this.onVisibility = this.#onVisibility.bind(this);
    this.onInteractionStart = this.#onInteractionStart.bind(this);

    this.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('pointerdown', this.onInteractionStart, { passive: true });
    this.mql.addEventListener('change', this.onResize);
    this.motionQuery.addEventListener('change', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.#sync();
  }

  disconnectedCallback() {
    this.#stop();
    clearTimeout(this.#scrollSettle);
    this.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('pointerdown', this.onInteractionStart);
    this.mql?.removeEventListener('change', this.onResize);
    this.motionQuery?.removeEventListener('change', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Carousel (en de bijbehorende semantiek) is alleen actief op mobiel. */
  #sync() {
    if (this.mql.matches) {
      this.dots?.removeAttribute('hidden');
      this.#applyCarouselSemantics();
      this.#startIfPossible();
    } else {
      this.dots?.setAttribute('hidden', '');
      this.#stop();
      this.#removeCarouselSemantics();
    }
    this.#refreshLive();
  }

  #startIfPossible() {
    this.#stop();
    const designMode = window.Shopify && window.Shopify.designMode;
    if (this.interval > 0 && !this.motionQuery.matches && !designMode && !this.#userPaused) {
      this.#timer = setInterval(() => this.#advance(), this.interval);
    }
    this.#refreshLive();
  }

  #stop() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #advance() {
    this.#goTo((this.#index + 1) % this.slides.length);
  }

  #goTo(index) {
    this.#index = index;
    this.#suppressScrollSync = true;
    this.scrollTo({ left: this.clientWidth * index, behavior: 'smooth' });
    clearTimeout(this.#scrollSettle);
    this.#scrollSettle = setTimeout(() => {
      this.#suppressScrollSync = false;
    }, SCROLL_SETTLE_MS);
    this.#updateDots();
  }

  #onScroll() {
    if (this.#suppressScrollSync || this.clientWidth === 0) return;
    const index = Math.round(this.scrollLeft / this.clientWidth);
    if (index !== this.#index) {
      this.#index = index;
      this.#updateDots();
    }
  }

  /**
   * Gebruiker neemt over (swipe of stip): autoplay stopt definitief.
   * Er volgt bewust géén automatische herstart — de gebruiker houdt controle.
   */
  #onInteractionStart() {
    this.#userPaused = true;
    this.#stop();
    this.#refreshLive();
  }

  #onVisibility() {
    if (document.hidden) {
      this.#stop();
      this.#refreshLive();
    } else {
      this.#sync();
    }
  }

  #applyCarouselSemantics() {
    this.setAttribute('role', 'group');
    this.setAttribute('aria-roledescription', this.carouselRoleDescription);
    if (this.carouselLabel) this.setAttribute('aria-label', this.carouselLabel);
    this.slides.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', this.slideRoleDescription);
      slide.setAttribute('aria-label', this.#labelFor(i));
    });
  }

  #removeCarouselSemantics() {
    this.removeAttribute('role');
    this.removeAttribute('aria-roledescription');
    this.removeAttribute('aria-label');
    this.removeAttribute('aria-live');
    this.slides.forEach((slide) => {
      slide.removeAttribute('role');
      slide.removeAttribute('aria-roledescription');
      slide.removeAttribute('aria-label');
    });
  }

  /** off tijdens auto-rotatie, polite zodra gepauzeerd of handmatig bediend. */
  #refreshLive() {
    if (!this.mql.matches) return;
    this.setAttribute('aria-live', this.#timer ? 'off' : 'polite');
  }

  #labelFor(i) {
    return this.slideLabelTemplate
      .replaceAll('__N__', `${i + 1}`)
      .replaceAll('__T__', `${this.slides.length}`);
  }

  #buildDots() {
    if (!this.dots) return;
    this.dots.innerHTML = '';
    this.dotEls = this.slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'custom-hero-grid__dot';
      dot.setAttribute('aria-label', this.#labelFor(i));
      dot.addEventListener('click', () => {
        this.#onInteractionStart();
        this.#goTo(i);
      });
      this.dots.appendChild(dot);
      return dot;
    });
    this.#updateDots();
  }

  #updateDots() {
    if (!this.dotEls) return;
    this.dotEls.forEach((dot, i) => {
      const active = i === this.#index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('custom-hero-grid-slider')) {
  customElements.define('custom-hero-grid-slider', CustomHeroGridSlider);
}
